import styled, { css } from 'styled-components';
import Markdown from 'react-markdown';

import { CARD_SHADOW_COLOR, WHITE, TOOLTIP_CONTENT_COLOR, UI_COLORS } from '../../utils/colors';
import { ContentContainer, ContentMain, sansSerifFont, serifFont } from '../layout/layout.styles';
import { media } from '../../utils/media';
import { always, equals } from 'ramda';
import { propCond } from '../../utils/propCond';
import { ZINDEX } from '../../utils/constants';

const VideoContentSide = styled.div`
  --content-size: 40px;
  width: var(--content-size);
  min-width: var(--content-size);
  display: flex;
  justify-content: flex-end;
  margin-right: auto;
  margin-top: 80px;

  ${media.tablet`
    justify-content: center;
  `}

  ${media.desktop`
    --content-size: 240px;
    display: block;
  `}

  @media print {
    --content-size: 240px;
    display: block;
  }
`;


const h1h2Styles = css`
  h1,
  h2 {
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0.4px;
    text-transform: uppercase;

    ${media.tablet`
      font-size: 30px;
      margin-bottom: 32px;
    `}
  }
`;

const h3h4Styles = ({ theme }) => css`
  h3,
  h4 {
    color: ${theme.color};
    font-size: 18px;
    font-weight: 600;
    line-height: 1em;
    letter-spacing: 0.9px;
    margin-bottom: 32px;
  }

  h4 {
    margin-bottom: 6px;
  }
`;

const linkStyles = ({ theme }) => css`
  color: ${theme.color};
  text-decoration: none;
  border-bottom: 1px solid ${theme.color};
  line-height: 1em;
  word-break: break-word;
`;

const pStyles = css`
  > p {
    ${serifFont};
    font-size: 18px;
    font-weight: 500;
    line-height: 1.6em;
    margin-bottom: 1.6em;

    ${media.tablet`
      font-size: 20px;
    `}

    a {
      ${linkStyles}
    }
  }
`;

const h5Styles = css`
  h5 {
    font-family: 'FreightText';
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 32px;
  }
`;

const h6Styles = css`
  font-size: 12px;
  line-height: 1.5em;
  letter-spacing: 0.3px;
`;

const listsStyles = ({ theme }) => css`
  ol {
    counter-reset: item;
    list-style-type: none;
    margin: 0 0 13px;

    li {
      ${serifFont};
      color: ${theme.color};
      font-size: 24px;
      line-height: 1.33em;
      padding: 21px 0 21px 40px;
      border-top: 2px solid ${theme.color};
      margin: 0;

      ${media.desktop`
        padding-left: 85px;
        font-size: 30px;
      `}

      @media print {
        padding-left: 85px;
        font-size: 30px;
      }
    }

    li:before {
      ${sansSerifFont};
      line-height: 1.2em;
      font-weight: bold;
      content: counter(item);
      counter-increment: item;
      margin: 0 10px 0 -40px;
      width: 30px;
      display: inline-block;

      ${media.desktop`
        margin-left: -85px;
        width: 75px;
      `}

      @media print {
        margin-left: -85px;
        width: 75px;
      }
    }
  }

  h3 + ol {
    margin-top: -8px;
  }

  ol + h6 {
    color: ${theme.color};
  }

  ul li {
    ${serifFont};
    font-size: 20px;
    font-weight: 500;
    line-height: 1.6em;
    margin-bottom: 1.6em;
  }
`;

export const Tooltip = styled.span`
  position: absolute;
  background-color: ${WHITE};
  padding: 10px 15px;
  margin-top: 5px;
  width: 300px;
  display: none;
  box-shadow: 0 2px 6px 0 ${CARD_SHADOW_COLOR};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5em;
  z-index: ${ZINDEX.CONTENT_TOOLTIP};
  color: ${TOOLTIP_CONTENT_COLOR};
  ${sansSerifFont}
`;

export const PseudoLink = styled.span`
  ${linkStyles};
  cursor: pointer;
  position: relative;

  :hover ${Tooltip} {
    display: block;
  }
`;

export const Link = styled.a`
  ${linkStyles}
`;

export const Side = styled(VideoContentSide)``;

export const MarkdownContent = styled(Markdown)`
  width: 100%;
`;

export const Main = styled(ContentMain)`
  width: 100%;
  padding-right: 20px;

  ${media.desktop`
    padding-right: 40px;
  `}

  ${MarkdownContent} {
    ${h1h2Styles}
    ${h3h4Styles}
    ${h5Styles}
    ${h6Styles}
    ${pStyles}
    ${listsStyles}
  }
`;

const isExpandedCond = propCond('isExpanded');

export const Container = styled(ContentContainer)`
  position: relative;
  margin-bottom: 100px;

  ${isExpandedCond([
    [
      equals(false),
      always(css`
        margin-bottom: 50px;

        ${Main} {
          position: relative;
        }

        ${MarkdownContent} {
          max-height: 500px;
          overflow: hidden;

          @media print {
            overflow: unset;
            max-height: none;
          }
        }
        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 200px;
          width: 100%;
          background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff);
          z-index: ${ZINDEX.CONTENT_EXPAND_FADE};
        }
      `),
    ],
  ])}
`;

export const ExpandButtonWrapper = styled.div`
  position: absolute;
  left: 0;
  bottom: -75px;
  display: flex;
  justify-content: center;
  width: calc(100% - 41px);

  ${media.tablet`
    width: 100%;
  `}
`;

export const ExpandButton = styled.button`
  ${sansSerifFont};
  z-index: ${ZINDEX.CONTENT_EXPAND_BUTTON};
  padding: 15px 35px;
  opacity: 0.5;
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 1.2px;
  color: ${UI_COLORS[0]};
  border: 1px solid #c4c4c4;
  text-transform: uppercase;
`;

export const TitleWrapper = styled(ContentContainer)`
  padding: 15px 0 21px 0;
  margin: 0 40px 0 50px;

  ${media.tablet`
    margin: 0 30px;
  `}

  ${media.desktop`
    margin: 0;
  `}
`;

export const Title = styled(Markdown)`
  h2 {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.5em;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color};
  }

  p {
    margin-bottom: 0;
  }
`;

export const VideoComponent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: calc(45vw - 20px);

  ${media.tablet`
    width: 100%;
    height: 450px;
  `}

  .embed_video {
    position: relative;
    width: 100%;
    height: 0;
  }

  iframe {
    border: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin-bottom: 0;

    ${media.desktop`
      margin-bottom: unset;
    `}
  }
`;

export const CoverWrapper = styled.div`
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  ${media.desktopWide`
    width: 660px;
  `}
`;

export const ClickCover = styled.img`

  width: calc(100% - 24px);
  ${media.desktopWide`
    width: 660px;
  `}
  
`;